<template>
  <div v-redirect-if-no-permission:VIEW_CAPTAIN_LEDGER class="finance-ledger-page fin-page">
    <finance-section-nav />
    <loading-component :value="loading.balance || loading.entries"></loading-component>

    <div class="fin-filters mb-2">
      <choose-date class="fin-field" clearable :label="$t('finance.common.from')" v-model="filters.from"></choose-date>
      <choose-date class="fin-field" clearable :label="$t('finance.common.to')" v-model="filters.to"></choose-date>
      <v-select
        class="fin-field"
        v-model="filters.type"
        :items="typeItems"
        :label="$t('finance.ledger.entryType')"
        clearable
        hide-details
      ></v-select>
      <v-btn small color="primary" :loading="loading.entries" @click="loadLedger">{{ $t("finance.common.apply") }}</v-btn>
      <v-btn small flat @click="goBack">{{ $t("finance.common.back") }}</v-btn>
    </div>

    <v-card class="mb-2">
      <div class="fin-summary">
        <div class="fin-stat">
          <span class="fin-stat-label">{{ $t("finance.ledger.currentBalance") }}</span>
          <span class="fin-stat-value">{{ formatCurrency(snapshot.current_balance) }}</span>
        </div>
        <!-- Accounting doc 2026-09-12, answer 1: the welcome bonus is restricted
             to covering the company commission. Showing the payable figure next
             to the ledger position is what stops an operator paying it out.
             Owner item 2.1: the figure is what is LEFT of it (granted, minus
             the commission it covered, minus any closure). -->
        <div class="fin-stat" v-if="snapshot.restricted_bonus_balance" :title="$t('finance.ledger.restrictedBonusHint')">
          <span class="fin-stat-label">{{ $t("finance.ledger.restrictedBonus") }}</span>
          <span class="fin-stat-value restricted">{{ formatCurrency(snapshot.restricted_bonus_balance) }}</span>
        </div>
        <div class="fin-stat" v-if="snapshot.restricted_bonus_balance">
          <span class="fin-stat-label">{{ $t("finance.ledger.withdrawableBalance") }}</span>
          <span class="fin-stat-value">{{ formatCurrency(snapshot.withdrawable_balance) }}</span>
        </div>
        <div class="fin-stat">
          <span class="fin-stat-label">{{ $t("finance.ledger.pendingBookings") }}</span>
          <span class="fin-stat-value">{{ formatCurrency(snapshot.pending_bookings_total) }}</span>
        </div>
        <div class="fin-stat">
          <span class="fin-stat-label">{{ $t("finance.ledger.settledToDriver") }}</span>
          <span class="fin-stat-value">{{ formatCurrency(snapshot.total_settled_to_driver) }}</span>
        </div>
        <div class="fin-stat">
          <span class="fin-stat-label">{{ $t("finance.ledger.receivedFromDriver") }}</span>
          <span class="fin-stat-value">{{ formatCurrency(snapshot.total_received_from_driver) }}</span>
        </div>
      </div>
    </v-card>

    <v-card class="mt-4">
      <v-card-title>
        <div>
          <div class="headline">{{ captainName }}</div>
          <div class="caption">{{ captainSubTitle }}</div>
        </div>
      </v-card-title>
      <v-data-table
        :headers="headers"
        :items="entries"
        :rows-per-page-items="[10, 25, 50, 100]"
        :loading="loading.entries"
        class="fin-dt elevation-0"
      >
        <template v-slot:items="props">
          <tr>
            <td>{{ formatDate(props.item.posted_at) }}</td>
            <td>{{ formatEntryType(props.item.entry_type, props.item.direction) }}</td>
            <td>{{ props.item.booking_id || '-' }}</td>
            <td class="fin-num">{{ formatCurrency(props.item.credit_amount) }}</td>
            <td class="fin-num">{{ formatCurrency(props.item.debit_amount) }}</td>
            <td class="fin-num">{{ formatCurrency(props.item.balance_after) }}</td>
            <td>{{ props.item.note || '-' }}</td>
            <td>{{ props.item.created_by_name || $t("finance.common.performedBySystem") }}</td>
          </tr>
        </template>
      </v-data-table>
    </v-card>
  </div>
</template>

<script>
import FinanceSectionNav from "@/components/finance/FinanceSectionNav";
import ChooseDate from "@/components/monaco/input/ChooseDate";
import LoadingComponent from "@/components/LoadingComponent";
import { formatFinanceDate } from "@/mixins/financeDate";

export default {
  name: "CaptainLedger",
  title() {
    return this.$t("finance.ledger.pageTitle");
  },
  components: {
    FinanceSectionNav,
    ChooseDate,
    LoadingComponent,
  },
  data() {
    return {
      loading: {
        balance: false,
        entries: false,
      },
      filters: {
        from: null,
        to: null,
        type: null,
      },
      snapshot: {
        captain: null,
        current_balance: 0,
        withdrawable_balance: 0,
        restricted_bonus_balance: 0,
        pending_bookings_total: 0,
        total_settled_to_driver: 0,
        total_received_from_driver: 0,
      },
      entries: [],
    };
  },
  computed: {
    headers() {
      return [
        { text: this.$t("finance.common.date"), value: "posted_at" },
        { text: this.$t("finance.common.type"), value: "entry_type" },
        { text: this.$t("finance.ledger.booking"), value: "booking_id" },
        { text: this.$t("finance.common.credit"), value: "credit_amount", align: "end" },
        { text: this.$t("finance.common.debit"), value: "debit_amount", align: "end" },
        { text: this.$t("finance.ledger.balanceAfter"), value: "balance_after", align: "end" },
        { text: this.$t("finance.common.note"), value: "note" },
        // Item 4.1: the employee behind the row (blank for automatic rows).
        { text: this.$t("finance.common.performedBy"), value: "created_by_name" },
      ];
    },
    typeItems() {
      return [
        { text: this.$t("finance.ledger.bookingRevenue"), value: "booking_revenue" },
        { text: this.$t("finance.ledger.settlement"), value: "settlement" },
        { text: this.$t("finance.ledger.adjustment"), value: "adjustment" },
      ];
    },
    captainId() {
      return this.$route.params.captainId;
    },
    captainName() {
      if (!this.snapshot.captain) {
        return this.$t("finance.ledger.pageTitle");
      }

      return this.snapshot.captain.name;
    },
    captainSubTitle() {
      if (!this.snapshot.captain) {
        return "";
      }

      return [this.snapshot.captain.phone, this.snapshot.captain.email]
        .filter(Boolean)
        .join(" | ");
    },
  },
  mounted() {
    // Route requires :captainId; without it the API calls would hit
    // /captains/undefined/... — send the user to the balances list instead.
    if (!this.$route.params.captainId || this.$route.params.captainId === "undefined") {
      this.$router.replace({ name: "payments" });
      return;
    }
    this.loadSnapshot();
    this.loadLedger();
  },
  methods: {
    getErrorMessage(error, fallback) {
      if (
        error &&
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        return error.response.data.message;
      }

      return fallback;
    },
    loadSnapshot() {
      this.loading.balance = true;
      this.axios
        .get(this.api.ACTIONS.ACCOUNTING_CAPTAIN_BALANCE(this.captainId))
        .then(({ data }) => {
          this.snapshot = data.data;
        })
        .catch((error) => {
          window.getApp.$emit(
            "SHOW_TOAST",
            this.getErrorMessage(error, this.$t("finance.ledger.failedLoadBalance")),
            "red"
          );
        })
        .finally(() => {
          this.loading.balance = false;
        });
    },
    loadLedger() {
      this.loading.entries = true;
      this.axios
        .get(this.api.ACTIONS.ACCOUNTING_CAPTAIN_LEDGER(this.captainId), {
          params: {
            from: this.filters.from,
            to: this.filters.to,
            type: this.filters.type,
            perPage: 100,
          },
        })
        .then(({ data }) => {
          this.entries = data.data.data || [];
        })
        .catch((error) => {
          window.getApp.$emit(
            "SHOW_TOAST",
            this.getErrorMessage(error, this.$t("finance.ledger.failedLoadEntries")),
            "red"
          );
        })
        .finally(() => {
          this.loading.entries = false;
        });
    },
    formatCurrency(value) {
      return `SYP ${this.formatMoney(value || 0)}`;
    },
    formatDate(value) {
      // One shared finance formatter (accountant doc 2026-10-03 item 1.8).
      return formatFinanceDate(value);
    },
    formatEntryType(type, direction) {
      if (type === "booking_revenue") {
        return this.$t("finance.ledger.bookingRevenue");
      }

      if (type === "settlement" && direction === "paid_to_driver") {
        return this.$t("finance.ledger.paidToDriver");
      }

      if (type === "settlement" && direction === "received_from_driver") {
        return this.$t("finance.ledger.receivedFromDriverLabel");
      }

      return this.$t("finance.ledger.adjustment");
    },
    goBack() {
      this.$router.push({ name: "payments" });
    },
  },
};
</script>

<style scoped>
.finance-ledger-page {
  padding-top: 16px;
}

.fin-stat-value.restricted {
  color: #b26500;
}
</style>